import { type JSX, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// Pattern: docs/design-patterns/frontend/component-patterns.md (layout-structure-only)
/**
 * DocsLayout - Main layout for documentation pages.
 *
 * Renders page content full-width. Primary navigation between Knowledge
 * pages (Beta, What is Compounds?, etc.) lives in the Global Shell sidebar
 * (~/components/shell/navConfig) — this layout no longer duplicates it with
 * a nested doc-only sidebar.
 *
 * Note: Header and Footer are provided by ProtectedLayout parent.
 */
export function DocsLayout(): JSX.Element {
  const mainRef = useRef<HTMLElement>(null);
  const location = useLocation();

  // Reset scroll on every route change — the main element is the scroll container
  useEffect(() => {
    if (location.hash) {
      // Hash navigation: scroll to the anchor element
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      // Page navigation: scroll to top
      mainRef.current?.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <main ref={mainRef} className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Outlet />
      </div>
    </main>
  );
}
